import { useState, useEffect } from 'react';
import { safePostHogOnFeatureFlags, safePostHogIsFeatureEnabled } from 'utils/posthogHelpers';

/**
 * Feature flag name for Canadian investor flow
 * Configure this in PostHog dashboard:
 * - Flag name: 'canadian-investor-flow'
 * - When enabled: Canadian IP detection and /private-canada redirect are active
 * - When disabled: Canadian users can invest in US offerings at /private
 */
export const CANADIAN_FEATURE_FLAG = 'canadian-investor-flow';

/**
 * Hook to check if the Canadian investor flow feature flag is enabled
 *
 * This controls:
 * - Canadian IP detection modal (nudges Canadian IPs to /private-canada)
 * - Redirect to /private-canada/invest from offering pages
 * - Canadian-specific accreditation and address collection
 *
 * When disabled (default):
 * - Canadian QPs (>5M CAD) can invest in US-based offerings at /private
 * - No modal appears for Canadian IPs
 * - No redirect to /private-canada/invest
 *
 * When enabled:
 * - Canadian IPs see a modal nudging them to /private-canada
 * - Clicking "Invest Now" on /private-canada/offering pages redirects to /private-canada/invest
 * - Canadian address and accreditation status are collected
 *
 * @returns {Object} { isEnabled: boolean, isLoading: boolean }
 */
const useCanadianFeatureFlag = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    safePostHogOnFeatureFlags(() => {
      const enabled = safePostHogIsFeatureEnabled(CANADIAN_FEATURE_FLAG);
      setIsEnabled(Boolean(enabled));
      setIsLoading(false);
    });
  }, []);

  return {
    isEnabled,
    isLoading,
  };
};

export default useCanadianFeatureFlag;
